// Calculator autosave (Session AAV, Loop 2). The in-progress quote is autosaved to a real
// server DRAFT (the same POST /api/quotes draft path Save Draft uses, revising in place), so
// nothing is invented client-side; this module holds the pure decision logic + the local
// restore pointer so the calculator can offer "restore your unsaved draft" on return.
//
// Only COHERENT snapshots autosave: the same conditions Save Draft requires (a customer name,
// no invalid email, the live price settled for exactly the state on screen), so a restored
// draft is always a priceable, saveable quote -- never a half-typed torso.

import { storageKeySuffix } from "./phonePreview";

export const AUTOSAVE_DEBOUNCE_MS = 2500;

export interface AutosavePointer {
  id: string; // the draft quote id (the autosave revises it in place)
  ref: string; // its allocated quote reference, for the banner
  savedAt: string; // ISO instant of the last autosave, for the banner
}

// Pure gate: may an autosave fire now? Mirrors canSaveDraft plus the eligibility rule
// (autosave only ever writes NEW quotes or resumed DRAFTS; editing an already-sent quote
// must never silently flip it back to draft or spawn version snapshots).
export function canAutosave(s: {
  eligible: boolean; // new quote, or resuming a draft (never editing a sent quote)
  detailsOk: boolean; // a customer name is present (the server requires one)
  emailInvalid: boolean;
  priceFresh: boolean; // the on-screen price matches the current inputs
  saving: boolean; // an explicit save/send is in flight
}): boolean {
  return s.eligible && s.detailsOk && !s.emailInvalid && s.priceFresh && !s.saving;
}

// ---- The restore pointer (localStorage; per user, per browser) ----
// Storage failures (private mode, quota) are swallowed: autosave is a convenience and must
// never break the calculator.

function storageKey(userId: string): string {
  // Inside the Phone view the pointer is kept under its own key (Session BDZ), so it never appears in the real app.
  return `ele.quoteAutosave.${userId}${storageKeySuffix()}`;
}

export function saveAutosavePointer(userId: string, pointer: AutosavePointer): void {
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(pointer));
  } catch {
    /* best-effort */
  }
}

export function loadAutosavePointer(userId: string): AutosavePointer | null {
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AutosavePointer> | null;
    if (!parsed || typeof parsed.id !== "string" || typeof parsed.ref !== "string" || typeof parsed.savedAt !== "string") return null;
    return { id: parsed.id, ref: parsed.ref, savedAt: parsed.savedAt };
  } catch {
    return null;
  }
}

export function clearAutosavePointer(userId: string): void {
  try {
    window.localStorage.removeItem(storageKey(userId));
  } catch {
    /* best-effort */
  }
}
